/**
 * TOKEN BRIDGE FOR A CANVAS CHART — and the Law 8 hole it exists to close.
 *
 * ⛔ `lightweight-charts` PAINTS TO A CANVAS, AND A CANVAS CANNOT READ `var(--token)`. Every colour
 * handed to LWC must be a resolved STRING, which means the one thing our token gate checks for —
 * "no colour literal, and every `var()` is defined" — CANNOT SEE THIS FILE'S FAILURE MODE.
 *
 * ⇒ ***A CHART THAT RESOLVES TOKENS ONCE IS CORRECT IN THE THEME IT BOOTED IN AND WRONG IN THE
 * OTHER, AND BOTH GATES STAY GREEN.*** There is no literal to find and no invalid `var()` to flag:
 * `check-tokens.mjs` rule 1 passes (we reference token NAMES), rule 3 passes (they are all defined),
 * and the chart still paints last theme's colours after a flip. That is a law-8 defect wearing a
 * law-8-compliant costume, and it is why this module is a module and not three inline lookups.
 *
 * THE CURE IS STRUCTURAL, NOT DILIGENCE: resolve through ONE function, and re-resolve on every
 * theme change from ONE subscription. Both sides call the same construction, so a mutant that
 * breaks resolution breaks the arms too (hand-1's law: an arm that re-implements the thing it
 * guards is a second opinion, not a test).
 */

/** Every token the spike paints with. Named, so a missing one fails LOUD rather than as `""`. */
export const CHART_TOKENS = [
  "--up", "--dn", "--up-soft", "--dn-soft", "--vwap", "--magnet", "--flip",
  "--cvd", "--cvd-pos", "--cvd-neg", "--cvd-pos-hot", "--cvd-neg-hot",
  "--vsplit-above", "--vsplit-below",
  "--stale", "--stale-ink", "--stale-soft",
  "--ink", "--muted", "--faint", "--line", "--surface", "--surface-2", "--chip", "--accent",
] as const;

export type ChartToken = (typeof CHART_TOKENS)[number];
export type ResolvedTokens = Record<ChartToken, string>;

/**
 * Resolve every chart token against a live element.
 *
 * ⚠ FAIL-LOUD ON AN EMPTY RESOLUTION. `getComputedStyle().getPropertyValue()` returns `""` for an
 * undefined custom property — it does NOT throw. Handing `""` to LWC yields a transparent mark:
 * the line is "drawn" and invisible, which is the silent-omission class this cockpit exists to
 * remove, one layer below where we usually look for it.
 */
export function resolveTokens(el: HTMLElement): ResolvedTokens {
  const cs = getComputedStyle(el);
  const out = {} as ResolvedTokens;
  const missing: string[] = [];
  for (const t of CHART_TOKENS) {
    const v = cs.getPropertyValue(t).trim();
    if (!v) missing.push(t);
    out[t] = v;
  }
  if (missing.length) {
    throw new Error(
      `lwcTokens: ${missing.length} token(s) resolved EMPTY — ${missing.join(", ")}. ` +
        `A canvas chart cannot fall back to inherit; an empty colour paints an invisible mark.`,
    );
  }
  return out;
}

/**
 * Watch for a theme change and re-resolve. Returns a teardown.
 *
 * TWO SOURCES, because the token package itself has two: the `data-theme` STAMP and the OS
 * `prefers-color-scheme` MEDIA QUERY. Watching only the stamp misses a user who never touched our
 * toggle; watching only the query misses our own override. `tokens.css` says neither block may be
 * deleted as redundant — the same is true of the two things that trigger them.
 */
export function watchTheme(el: HTMLElement, onChange: (t: ResolvedTokens) => void): () => void {
  const fire = () => onChange(resolveTokens(el));

  const mo = new MutationObserver(fire);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  mq.addEventListener("change", fire);

  return () => {
    mo.disconnect();
    mq.removeEventListener("change", fire);
  };
}
